'use client';

import {
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';

import { validEmail } from '@/constants/regexp/regexp.constants';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  model: string;
  brochureLabel: string;
  alwaysShowDeliveryOptions?: boolean;
  onSubmitEmail: (name: string, email: string) => Promise<void>;
  onSubmitWhatsApp: (
    name: string,
    whatsappNumber: string,
  ) => Promise<void>;
};

type Step = 'choice' | 'email' | 'whatsapp';

export default function BrochureEmailModal({
  isOpen,
  onClose,
  model,
  brochureLabel,
  alwaysShowDeliveryOptions,
  onSubmitEmail,
  onSubmitWhatsApp
}: Props) {
  const [step, setStep] = useState<Step>(alwaysShowDeliveryOptions ? 'choice' : 'email');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappTouched, setWhatsappTouched] = useState(false);
  const [emailNameTouched, setEmailNameTouched] = useState(false);

  const normalizedWhatsAppNumber = (() => {
    const value = whatsappNumber.replace(/[\s()-]/g, '');

    if (/^0[6-8]\d{8}$/.test(value)) {
      return `+27${value.slice(1)}`;
    }

    if (/^\+27[6-8]\d{8}$/.test(value)) {
      return value;
    }

    return '';
  })();

  const isWhatsAppNumberValid = Boolean(normalizedWhatsAppNumber);
  const isNameValid = name.trim().length > 0;

  const toast = useToast();
  const isEmailValid = useMemo(
    () => validEmail.test(email),
    [email],
  );

  useEffect(() => {
    if (isOpen) {
      setStep(alwaysShowDeliveryOptions ? 'choice' : 'email');
      setEmail('');
      setName('');
      setWhatsappNumber('');
      setTouched(false);
      setWhatsappTouched(false);
      setError(null);
      setEmailNameTouched(false);
    }
  }, [isOpen, alwaysShowDeliveryOptions]);

  const handleChooseEmail = () => {
    setStep('email');
    setError(null);
  };

  const handleChooseWhatsApp = () => {
    setStep('whatsapp');
    setError(null);
  };

  const handleBack = () => {
    if (alwaysShowDeliveryOptions) {
      setStep('choice');
    }

    setEmail('');
    setName('');
    setWhatsappNumber('');
    setTouched(false);
    setWhatsappTouched(false);
    setError(null);
  };

  const handleSubmit = async () => {
    setEmailNameTouched(true);
    setTouched(true);

    if (!isNameValid || !isEmailValid) return;

    setError(null);
    setSubmitting(true);

    try {
      await onSubmitEmail(name.trim(), email.trim());

      toast({
        title: 'Success!',
        description: `The ${brochureLabel.toLowerCase()} has been sent to your email.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setEmail('');
      setName('');
      setTouched(false);
      setEmailNameTouched(false);
      onClose();
    } catch {
      setError('Could not send the brochure. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitWhatsApp = async () => {
    setWhatsappTouched(true);

    if (!isNameValid || !isWhatsAppNumberValid) {
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onSubmitWhatsApp(name.trim(), normalizedWhatsAppNumber);

      toast({
        title: 'Success!',
        description: `The ${brochureLabel.toLowerCase()} request has been submitted for WhatsApp delivery.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setName('');
      setWhatsappNumber('');
      setWhatsappTouched(false);
      setEmailNameTouched(false);
      onClose();
    } catch {
      setError(
        'Could not send the brochure via WhatsApp. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={!submitting} closeOnEsc={!submitting}>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>
          {step === 'choice'
            ? 'Receive your brochure'
            : step === 'email'
              ? 'Get your brochure by email'
              : 'Get your brochure on WhatsApp'}
        </ModalHeader>

        <ModalCloseButton disabled={submitting} />

        <ModalBody>
          {step === 'choice' && (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.600">
                How would you like to receive the {model}{' '}
                {brochureLabel.toLowerCase()}?
              </Text>


              <Button
                colorScheme="blackAlpha"
                bg="#25D366"
                color="white"
                _hover={{ bg: '#20BA5A' }}
                _active={{ bg: '#1DA851' }}
                onClick={handleChooseWhatsApp}
                width="100%"
                leftIcon={
                  <img
                    src="/icons/whatsapp.svg"
                    alt=""
                    width={30}
                    height={30}
                  />
                }
              >
                WhatsApp
              </Button>

              <Button
                variant="outline"
                onClick={handleChooseEmail}
              >
                Email
              </Button>
            </VStack>
          )}

          {step === 'email' && (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Enter your email to receive the {model}{' '}
                {brochureLabel.toLowerCase()}.
              </Text>

                 <FormControl
                isInvalid={emailNameTouched && !isNameValid}
                isRequired
              >
                <Input
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setEmailNameTouched(true)}
                  isDisabled={submitting}
                />

                <FormErrorMessage>Please enter your name.</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={touched && !isEmailValid}>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched(true)}
                  isDisabled={submitting}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      void handleSubmit();
                    }
                  }}
                />

                <FormErrorMessage>
                  Enter a valid email address.
                </FormErrorMessage>
              </FormControl>

              {error && (
                <Text color="red.500" fontSize="sm">
                  {error}
                </Text>
              )}
            </VStack>
          )}

         {step === 'whatsapp' && (
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" color="gray.600">
              Enter your details to receive the {model}{' '}
              {brochureLabel.toLowerCase()} via WhatsApp.
            </Text>

            <FormControl isInvalid={whatsappTouched && !isNameValid}>
              <Input
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                isDisabled={submitting}
              />
              <FormErrorMessage>Please enter your name.</FormErrorMessage>
            </FormControl>

            <FormControl
              isInvalid={whatsappTouched && !isWhatsAppNumberValid}
            >
              <Input
                type="tel"
                placeholder="WhatsApp number (e.g. 0821234567)"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                onBlur={() => setWhatsappTouched(true)}
                isDisabled={submitting}
              />
              <FormErrorMessage>
                Enter a valid South African mobile number.
              </FormErrorMessage>
            </FormControl>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}

            <Button
              variant="outline"
              onClick={handleBack}
              isDisabled={submitting}
            >
              Back
            </Button>

           <Button
              colorScheme="blackAlpha"
              bg="#25D366"
              color="white"
              _hover={{ bg: '#20BA5A' }}
              _active={{ bg: '#1DA851' }}
              onClick={() => void handleSubmitWhatsApp()}
              isLoading={submitting}
              isDisabled={!isNameValid || !isWhatsAppNumberValid}
              width="100%"
              leftIcon={
                <img
                  src="/icons/whatsapp.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              }
            >
              Send Brochure
            </Button>
          </VStack>
        )}
        </ModalBody>

        {step === 'email' && (
          <ModalFooter>
            <VStack align="stretch" spacing={3} width="100%">
              {alwaysShowDeliveryOptions && (
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  isDisabled={submitting}
                >
                  Back
                </Button>
              )}

              <Button
                colorScheme="blackAlpha"
                bg="#111"
                _hover={{ bg: '#000' }}
                onClick={() => void handleSubmit()}
                isLoading={submitting}
                isDisabled={!isNameValid || !isEmailValid}
                width="100%"
              >
                Send Brochure
              </Button>
            </VStack>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}
